import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '草榴技术讨论区',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://caoliu-tech.com/article/${article.id}`,
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `关于${article.title}的常见问题`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `本文详细介绍了${article.title}的相关内容，如需更多帮助，请浏览草榴技术讨论区的${category?.name || '技术'}分类获取更多文章。`,
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <SEOHead
        title={`${article.title} - 草榴技术讨论区`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://caoliu-tech.com/article/${article.id}`}
        ogImage={article.coverImage}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <article>
          <div className="bg-background-100/60 border-b border-background-200/60">
            <div className="max-w-[900px] mx-auto px-4 md:px-6 py-8">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category?.slug || '', href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <div className="max-w-[900px] mx-auto px-4 md:px-6 py-8 md:py-12">
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-sm text-foreground-500">
                <div className="flex items-center gap-2">
                  <img src={article.avatar} alt={article.author} className="w-8 h-8 rounded-full object-cover" />
                  <span className="font-medium text-foreground-800">{article.author}</span>
                </div>
                <span>{article.publishDate}</span>
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line"></i>
                    </span>
                    {article.readCount.toLocaleString()} 阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-chat-3-line"></i>
                    </span>
                    {article.commentCount} 评论
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-heart-line"></i>
                    </span>
                    {article.likeCount} 点赞
                  </span>
                </div>
              </div>
            </header>

            <div className="mb-8 rounded-xl overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-64 md:h-80 object-cover object-top"
                title={article.title}
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200/60">
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-500">分享到：</span>
                <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" rel="nofollow">
                  <i className="ri-wechat-line"></i>
                </a>
                <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" rel="nofollow">
                  <i className="ri-weibo-line"></i>
                </a>
                <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" rel="nofollow">
                  <i className="ri-twitter-x-line"></i>
                </a>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100/60 py-12 md:py-16">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-50 rounded-xl border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 overflow-hidden cursor-pointer block"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        title={ra.title}
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-heading font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}